import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import md5 from 'blueimp-md5';
import { Users } from 'lucide-react';

const BACKEND_URL = '';

function stableAgendaItemId(item) {
  if (!item) return '';
  if (item.id) return String(item.id);
  const key = `${item.title || ''}|${item.time || ''}|${item.room || ''}`;
  return md5(key).slice(0, 12);
}

/**
 * BriteLabRegistrantsDialog — admin view of who registered for a specific
 * BriteLab agenda row. Fetched on open from
 * GET /api/admin/events/{event_id}/britelab/{item_id}/registrants.
 */
export default function BriteLabRegistrantsDialog({ open, onClose, eventId, item }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !eventId || !item) return;
    const itemId = stableAgendaItemId(item);
    setLoading(true);
    (async () => {
      try {
        const r = await fetch(
          `${BACKEND_URL}/api/admin/events/${eventId}/britelab/${itemId}/registrants`,
          { credentials: 'include' }
        );
        if (r.ok) setData(await r.json());
        else setData({ error: await r.text() });
      } catch (e) { setData({ error: String(e) }); }
      finally { setLoading(false); }
    })();
  }, [open, eventId, item]);

  if (!open || !item) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="britelab-registrants-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-brand-navy" />
            BriteLab Registrants
          </DialogTitle>
          <DialogDescription className="text-xs">
            {item.title} {item.time && `• ${item.time}`} {item.room && `• ${item.room}`}
          </DialogDescription>
        </DialogHeader>

        {loading && <p className="text-sm text-slate-500">Loading…</p>}

        {data?.error && (
          <p className="text-sm text-red-600" data-testid="registrants-error">
            Could not load: {data.error}
          </p>
        )}

        {data && !data.error && (
          <>
            <div className="flex items-center gap-3 pb-3 border-b">
              <div className="text-sm font-semibold text-brand-navy" data-testid="registrants-count">
                {data.count} {data.max ? ` / ${data.max}` : ''} registered
              </div>
              {data.max && (
                <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${data.count >= data.max ? 'bg-red-500' : data.count / data.max >= 0.8 ? 'bg-amber-500' : 'bg-green-500'}`}
                    style={{ width: `${Math.min(100, (data.count / data.max) * 100)}%` }}
                  />
                </div>
              )}
            </div>
            {data.registrants?.length === 0 ? (
              <p className="text-sm text-slate-400 italic py-6 text-center">No registrants yet.</p>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white border-b">
                    <tr className="text-left text-slate-500">
                      <th className="py-2 pr-2">Name</th>
                      <th className="py-2 pr-2">Organisation</th>
                      <th className="py-2 pr-2">Email</th>
                      <th className="py-2 pr-2">Registered</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.registrants.map(r => (
                      <tr key={r.user_id} className="border-b border-slate-50" data-testid={`registrant-row-${r.user_id}`}>
                        <td className="py-2 pr-2 font-medium text-slate-800">{r.name || '—'}</td>
                        <td className="py-2 pr-2 text-slate-600">{r.organisation || '—'}</td>
                        <td className="py-2 pr-2 text-slate-500">{r.email || '—'}</td>
                        <td className="py-2 pr-2 text-slate-400">
                          {r.registered_at ? new Date(r.registered_at).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' }) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
